import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronRight, Shield, Zap, Stethoscope } from "lucide-react";
import { Link } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MobileMenu from "@/components/MobileMenu";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

export default function Home() {
  // The userAuth hooks provides authentication state
  // To implement login/logout functionality, simply call logout() or redirect to getLoginUrl()
  let { user, loading, error, isAuthenticated, logout } = useAuth();

  const { t } = useLanguage();
  const { lp } = useLocalizedPath();

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container flex items-center justify-between h-16">
          <Link href={lp('/')}>
            <div className="text-2xl font-bold text-primary">
              Skin by Raquel
            </div>
          </Link>
          <div className="hidden md:flex gap-8 items-center">
            <Link href={lp('/#about')} className="text-sm font-medium hover:text-primary transition-colors">{t('nav.about')}</Link>
            <Link href={lp('/services')} className="text-sm font-medium hover:text-primary transition-colors">{t('nav.services')}</Link>
            <Link href={lp('/tarifas')} className="text-sm font-medium hover:text-primary transition-colors">{t('nav.pricing')}</Link>
            <Link href={lp('/faq')} className="text-sm font-medium hover:text-primary transition-colors">{t('nav.faq')}</Link>
            <Link href={lp('/blog')} className="text-sm font-medium hover:text-primary transition-colors">{t('nav.blog')}</Link>
            <Link href={lp('/contact')} className="text-sm font-medium hover:text-primary transition-colors">{t('nav.contact')}</Link>
            <LanguageSwitcher />
          </div>
          <Link href={lp('/booking')} className="hidden md:block">
            <Button className="bg-primary hover:bg-primary/90">{t('nav.book')}</Button>
          </Link>
          <MobileMenu
            navItems={[
              { href: lp('/#about'), label: t('nav.about') },
              { href: lp('/services'), label: t('nav.services') },
              { href: lp('/tarifas'), label: t('nav.pricing') },
              { href: lp('/faq'), label: t('nav.faq') },
              { href: lp('/blog'), label: t('nav.blog') },
              { href: lp('/contact'), label: t('nav.contact') },
            ]}
            ctaHref={lp('/booking')}
            ctaLabel={t('nav.book')}
          />
        </div>
      </nav>

      <main className="flex-1">
        {/* Hero Section - Comprehensive Dermatology Focus */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://d2xsxph8kpxj0f.cloudfront.net/310519663371139906/EkQg3ZdVJxVmKefouSALG6/hero-dermatology-clinic-GW4VCjbooAo4xDtfvJqQac.webp"
              alt="Modern dermatology clinic"
              className="w-full h-full object-cover"
              width={1856}
              height={1072}
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/50"></div>
          </div>

          <div className="container relative py-24 md:py-40">
            <div className="max-w-3xl">
              <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
                {t('home.hero.title')}
              </h1>
              <p className="text-xl text-foreground/90 mb-8 leading-relaxed">
                {t('home.hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={lp('/squeeze')}>
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-lg">
                    {t('home.hero.cta1')}
                  </Button>
                </Link>
                <Link href={lp('/services')}>
                  <Button size="lg" variant="outline" className="text-lg">
                    {t('home.hero.cta2')}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key Services Highlight */}
        <section className="section-spacing bg-gradient-to-b from-white to-slate-50">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-foreground mb-4">
                {t('home.services.title')}
              </h2>
              <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
                {t('home.services.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Skin Cancer Screening */}
              <Card className="p-8 hover:shadow-xl transition-all duration-300 border-l-4 border-l-red-500">
                <Shield className="h-14 w-14 text-red-600 mb-4" />
                <h3 className="text-2xl font-bold text-foreground mb-3">{t('home.services.skin_cancer')}</h3>
                <p className="text-foreground/70 mb-4">
                  {t('home.services.skin_cancer_desc')}
                </p>
                <Link href={lp('/squeeze/moles')} className="text-primary font-semibold hover:underline flex items-center gap-2">
                    {t('home.hero.cta2')} <ChevronRight className="h-4 w-4" />
                </Link>
              </Card>

              {/* Acne Treatment */}
              <Card className="p-8 hover:shadow-xl transition-all duration-300 border-l-4 border-l-blue-500">
                <Zap className="h-14 w-14 text-blue-600 mb-4" />
                <h3 className="text-2xl font-bold text-foreground mb-3">{t('home.services.acne')}</h3>
                <p className="text-foreground/70 mb-4">
                  {t('home.services.acne_desc')}
                </p>
                <Link href={lp('/squeeze/acne')} className="text-primary font-semibold hover:underline flex items-center gap-2">
                    {t('home.hero.cta2')} <ChevronRight className="h-4 w-4" />
                </Link>
              </Card>

              {/* Laser & Advanced Therapies */}
              <Card className="p-8 hover:shadow-xl transition-all duration-300 border-l-4 border-l-teal-500">
                <Stethoscope className="h-14 w-14 text-teal-600 mb-4" />
                <h3 className="text-2xl font-bold text-foreground mb-3">{t('home.services.laser')}</h3>
                <p className="text-foreground/70 mb-4">
                  {t('home.services.laser_desc')}
                </p>
                <Link href={lp('/squeeze/laser')} className="text-primary font-semibold hover:underline flex items-center gap-2">
                    {t('home.hero.cta2')} <ChevronRight className="h-4 w-4" />
                </Link>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Link href={lp('/services')}>
                <Button size="lg" className="bg-primary hover:bg-primary/90">
                  {t('home.services.view_all')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="section-spacing bg-white">
          <div className="container">
            <div className="max-w-3xl mx-auto">
              <div>
                <h2 className="text-4xl font-bold text-foreground mb-6">{t('home.about.title')}</h2>
                <p className="text-lg text-foreground/80 mb-4 leading-relaxed">
                  {t('home.about.bio1')}
                </p>
                <p className="text-lg text-foreground/80 mb-4 leading-relaxed">
                  {t('home.about.bio2')}
                </p>
                <p className="text-lg text-foreground/80 mb-6 leading-relaxed">
                  {t('home.about.bio3')}
                </p>
                <Link href={lp('/about')}>
                  <Button variant="outline" size="lg" className="group">
                    {t('home.about.bio_link')}
                    <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Dr. Raquel */}
        <section className="section-spacing bg-slate-50">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-foreground mb-4">{t('home.why_choose.title')}</h2>
              <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
                {t('home.why_choose.subtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6 text-center">
                <div className="text-4xl font-bold text-primary mb-2">UAB</div>
                <h3 className="font-bold mb-2">{t('home.why_choose.education')}</h3>
                <p className="text-sm text-foreground/70">{t('home.why_choose.education_desc')}</p>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-4xl font-bold text-primary mb-2">🌍</div>
                <h3 className="font-bold mb-2">{t('home.why_choose.international')}</h3>
                <p className="text-sm text-foreground/70">{t('home.why_choose.international_desc')}</p>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-4xl font-bold text-primary mb-2">📊</div>
                <h3 className="font-bold mb-2">{t('home.why_choose.evidence')}</h3>
                <p className="text-sm text-foreground/70">{t('home.why_choose.evidence_desc')}</p>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-4xl font-bold text-primary mb-2">⚡</div>
                <h3 className="font-bold mb-2">{t('home.why_choose.response')}</h3>
                <p className="text-sm text-foreground/70">{t('home.why_choose.response_desc')}</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Emergency & Quick Inquiry Section */}
        <section className="section-spacing bg-gradient-to-r from-red-50 to-orange-50 border-t border-red-200">
          <div className="container">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-4xl font-bold text-red-900 mb-4">{t('home.urgent.title')}</h2>
                <p className="text-lg text-red-800 mb-6 leading-relaxed">
                  {t('home.urgent.subtitle')}
                </p>
                <Link href={lp('/squeeze')}>
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white text-lg">
                    {t('home.urgent.button')}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-md border border-red-100">
                <h3 className="font-bold text-xl mb-4 text-gray-900">{t('home.urgent.guarantee')}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-red-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('home.urgent.hours2')}</strong>
                      <p className="text-gray-700">{t('home.urgent.hours2_desc')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('home.urgent.hours30')}</strong>
                      <p className="text-gray-700">{t('home.urgent.hours30_desc')}</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-gray-900">{t('home.urgent.hours24')}</strong>
                      <p className="text-gray-700">{t('home.urgent.hours24_desc')}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Preview */}
        <section className="section-spacing bg-white">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-foreground mb-4">{t('home.faq.title')}</h2>
              <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
                {t('home.faq.subtitle')}
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Link href={lp('/faq')}>
                <Button size="lg" className="w-full bg-primary hover:bg-primary/90 text-lg">
                  {t('home.faq.view')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-spacing bg-primary text-primary-foreground">
          <div className="container text-center">
            <h2 className="text-4xl font-bold mb-4">{t('home.cta.title')}</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
              {t('home.cta.subtitle')}
            </p>
            <Link href={lp('/booking')}>
              <Button size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90 text-lg">
                {t('home.cta.button')}
              </Button>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-foreground/5 border-t border-border">
        <div className="container py-12">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Skin by Raquel</h3>
              <p className="text-sm text-foreground/70">
                {t('footer.about')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footer.services')}</h4>
              <ul className="space-y-2 text-sm text-foreground/70">
                <li><Link href={lp('/services')} className="hover:text-primary">{t('footer.hair_loss')}</Link></li>
                <li><Link href={lp('/services')} className="hover:text-primary">{t('footer.laser')}</Link></li>
                <li><Link href={lp('/services')} className="hover:text-primary">{t('footer.general')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footer.resources')}</h4>
              <ul className="space-y-2 text-sm text-foreground/70">
                <li><Link href={lp('/faq')} className="hover:text-primary">{t('footer.faq')}</Link></li>
                <li><Link href={lp('/blog')} className="hover:text-primary">{t('footer.blog')}</Link></li>
                <li><Link href={lp('/about')} className="hover:text-primary">{t('footer.about_dr')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t('footer.contact')}</h4>
              <ul className="space-y-2 text-sm text-foreground/70">
                <li className="text-sm">{t('contact.info.location')}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-8 text-center text-sm text-foreground/70">
            {t('footer.copyright')}
          </div>
        </div>
      </footer>
    </div>
  );
}
